import { useState } from 'react';
import type { FormState, UserTypeConfig } from '@/shared/types/users.types';
import type { ColorHelpers } from '@/utils/colorHelpers';
import { PhoneInputGroup } from '@/backoffice/components/ui/PhoneInputGroup';

interface Props {
    form: FormState;
    set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
    config: UserTypeConfig;
    c: ColorHelpers;
}

const INPUT = 'w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent';

function PasswordField({
    label,
    value,
    onChange,
    placeholder,
    ringClass,
    error,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    ringClass: string;
    error?: boolean;
}) {
    const [visible, setVisible] = useState(false);

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="relative">
                <input
                    type={visible ? 'text' : 'password'}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`${INPUT} ${ringClass} pr-11 ${error ? 'border-red-400' : ''}`}
                />
                <button
                    type="button"
                    onClick={() => setVisible(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    tabIndex={-1}
                    aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                    <i className={visible ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'} />
                </button>
            </div>
        </div>
    );
}

export function StepContact({ form, set, config, c }: Props) {
    return (
        <div className="space-y-5">
            <h2 className="text-xl font-semibold text-gray-900">Vos informations de contact</h2>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.firstName}
                        onChange={e => set('firstName', e.target.value)}
                        placeholder="Votre prénom"
                        className={`${INPUT} ${c.ring}`}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.lastName}
                        onChange={e => set('lastName', e.target.value)}
                        placeholder="Votre nom de famille"
                        className={`${INPUT} ${c.ring}`}
                    />
                </div>
            </div>

            <PhoneInputGroup
                label="Téléphone"
                required
                className="w-full"
                countryCode={form.phoneCountryCode}
                onCountryCodeChange={v => set('phoneCountryCode', v)}
                value={form.phone}
                onChange={(phone) => set('phone', phone)}
                placeholder="620 00 00 01"
                hint="Utilisable pour vous connecter"
            />

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse e-mail
                    {config.emailRequired
                        ? <span className="text-red-500 ml-1">*</span>
                        : <span className="text-gray-400 text-xs ml-2">(optionnelle)</span>
                    }
                </label>
                <input
                    type="email"
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    placeholder="vous@exemple.com"
                    className={`${INPUT} ${c.ring}`}
                />
            </div>

            <PasswordField
                label="Mot de passe *"
                value={form.password}
                onChange={v => set('password', v)}
                placeholder="Minimum 8 caractères"
                ringClass={c.ring}
            />

            <div>
                <PasswordField
                    label="Confirmer le mot de passe *"
                    value={form.confirmPassword}
                    onChange={v => set('confirmPassword', v)}
                    placeholder="Répétez le mot de passe"
                    ringClass={c.ring}
                    error={!!(form.confirmPassword && form.confirmPassword !== form.password)}
                />
                {form.confirmPassword && form.confirmPassword !== form.password && (
                    <p className="text-red-500 text-xs mt-1">Les mots de passe ne correspondent pas.</p>
                )}
            </div>
        </div>
    );
}
